import {
  isBoxTextWrap,
  type CustomImageTemplateBox,
  type CustomTemplateBoxBase,
  type CustomTextTemplateBox,
  type ImageBoxParams,
  type LegacyTextMetrics,
  type ObjectBoxParams,
  type TextBoxParams,
} from '@lite-workspace/dto-fotoclass-templates';
import type { BoxPassthroughMeta, TextPassthroughMeta } from '../interfaces/drawable-data.interface';
import type { TextParams } from '../interfaces/text-params.interface';
import { buildDrawableEffects, mergeDrawableEffects } from './boxes/effects';

/**
 * Fields spelled identically on BOTH sides (`CustomTemplateBoxBase` and `DrawableBase`) that every codec
 * copies straight across. They are NOT passthrough: live code reads them by name — template provenance
 * (`preserve-custom-text`, `preserve-person-groups`, `preserve-decoratives`, `spread-content-memory`,
 * `photo-book-helper.facade`, `cover-text-fields`, `spread-to-template`), render suppression
 * (`empty-photo-frames`, `empty-text-fields`, `printed-person-ids`, `template-spread-preview`) and
 * `hasTemplateObjects` (`spread-queries`), `coverFit` (`refit-template-cover` — the cover-refit decision that
 * must survive every save, or the spine recompute re-reads moved geometry), `wornFromBackground`
 * (`setTemplateBackground` in the pack editor — a layer the tile dressed the spread with; it must survive the album
 * round-trip and «Сохранить как шаблон», or the next background swap leaves the old tile's decor behind). Keeping
 * them off `templateMeta` is deliberate: a fontName change drops that whole bag, and losing a student's text link
 * to a font swap is exactly the bug being fixed.
 */
const CARRIED_BOX_KEYS = [
  'fillOpacity',
  'blendInteriorElements',
  'blendIf',
  'knockout',
  'knockoutBackdrop',
  'blendClipped',
  'transparencyShapes',
  'layerStyleRendering',
  'effectReferencePoint',
  'effectDocumentOrigin',
  'layerStyleMasks',
  'idFromTemplate',
  'isTemplateObject',
  'skipRender',
  'backgroundExcluded',
  'coverFit',
  'wornFromBackground',
  'spreadWide',
  'textWrap',
] as const;

export type CarriedBoxFields = {
  -readonly [K in (typeof CARRIED_BOX_KEYS)[number]]?: CustomTemplateBoxBase[K];
};

/**
 * Same function for both directions — the names and types match, so encode and decode share it. Emits only
 * the keys the source actually carries: an absent field must stay absent, never become an explicit
 * `undefined` key (that would change the persisted JSON shape of every box that never had one).
 */
export function carriedBoxFields(source: CarriedBoxFields): CarriedBoxFields {
  const fields: CarriedBoxFields = {};
  for (const key of CARRIED_BOX_KEYS) {
    const value = source[key];
    // The first editor's own boolean `textWrap` on a legacy drawable is not a Text Wrap: it stays behind.
    if (value === undefined || (key === 'textWrap' && !isBoxTextWrap(value))) continue;
    fields[key] = value as never;
  }

  return fields;
}

const BOX_PASSTHROUGH_KEYS = [
  'psdLayerId',
  'lock',
  'colorLabel',
  'clipping',
  'groupId',
  'name',
  'flipHorizontal',
  'flipVertical',
] as const satisfies readonly (keyof CustomTemplateBoxBase)[];

/** Packs raw PSD box fields into a bag; `undefined` when the box carries none (keeps `templateMeta` absent, not `{}`). */
export function buildBoxPassthroughMeta(box: CustomTemplateBoxBase): BoxPassthroughMeta | undefined {
  const meta: { -readonly [K in keyof BoxPassthroughMeta]?: BoxPassthroughMeta[K] } = {};
  for (const key of BOX_PASSTHROUGH_KEYS) {
    const value = box[key];
    if (value !== undefined) meta[key] = value as never;
  }

  if (box.effects !== undefined) {
    // Fully typed styles must not retain a raw copy that could resurrect after a live removal.
    const { bevel: _bevel, satin: _satin, patternOverlay: _pattern, glows: _glows, ...rawEffects } = box.effects;
    if (rawEffects.colorOverlay?.rendering === 'live') delete rawEffects.colorOverlay;
    if (rawEffects.gradientOverlay?.rendering === 'live') delete rawEffects.gradientOverlay;
    if (rawEffects.extraColorOverlays?.some(o => o.rendering === 'live')) delete rawEffects.extraColorOverlays;
    if (rawEffects.extraGradientOverlays?.some(o => o.rendering === 'live')) delete rawEffects.extraGradientOverlays;
    if (box.layerStyleRendering === 'live') {
      delete rawEffects.shadows;
      delete rawEffects.strokes;
    }

    if (Object.keys(rawEffects).length > 0) meta.rawEffects = rawEffects;
  }

  return Object.keys(meta).length > 0 ? meta : undefined;
}

/**
 * Unpacks a bag back onto a box's own fields; a no-op returning `box` unchanged when `meta` is absent.
 * Does NOT touch `box.effects` — shadow/stroke are typed on `drawable.effects` and folded back via the
 * codec's own `mergeDrawableEffects` call (see `boxes/effects.ts`), not this generic passthrough bag.
 */
export function mergeBoxPassthroughMeta<T extends CustomTemplateBoxBase>(box: T, meta: BoxPassthroughMeta | undefined): T {
  if (!meta) return box;
  return {
    ...box,
    psdLayerId: meta.psdLayerId ?? box.psdLayerId,
    lock: meta.lock ?? box.lock,
    colorLabel: meta.colorLabel ?? box.colorLabel,
    clipping: meta.clipping ?? box.clipping,
    groupId: meta.groupId ?? box.groupId,
    name: meta.name ?? box.name,
    flipHorizontal: meta.flipHorizontal ?? box.flipHorizontal,
    flipVertical: meta.flipVertical ?? box.flipVertical,
  };
}

type ObjectWorkflowParams = Pick<ObjectBoxParams, 'compoundClipBakeRecipe' | 'imageFrameIntent'>;

/** Object/background box-level bag PLUS the two ObjectBoxParams-only PSD-import workflow fields (§3 #17). */
export function buildObjectPassthroughMeta(
  box: CustomTemplateBoxBase,
  params: ObjectWorkflowParams,
): BoxPassthroughMeta | undefined {
  const boxMeta = buildBoxPassthroughMeta(box);
  if (params.compoundClipBakeRecipe === undefined && params.imageFrameIntent === undefined) return boxMeta;
  return {
    ...boxMeta,
    compoundClipBakeRecipe: params.compoundClipBakeRecipe,
    imageFrameIntent: params.imageFrameIntent,
  };
}

/** Inverse of `buildObjectPassthroughMeta` — the two params-level fields it packed, ready to spread into `params`. */
export function objectPassthroughParams(meta: BoxPassthroughMeta | undefined): ObjectWorkflowParams {
  return {
    compoundClipBakeRecipe: meta?.compoundClipBakeRecipe,
    imageFrameIntent: meta?.imageFrameIntent,
  };
}

type ObjectBakedStyleParams = Pick<ObjectBoxParams, 'rawEffects' | 'bakeInsets'>;

/**
 * OBJECT-only: the baked layer style of a raster decor (`params.rawEffects`/`params.bakeInsets`). The drawable has no
 * slot for it, and without the bag every pass through the codec seam dropped it — «Рамка изображения» then had no
 * shadow to lift and no layer bounds to shrink to. Background boxes never carry it: their pixels keep the shadow.
 */
export function buildObjectBakedStyleMeta(
  meta: BoxPassthroughMeta | undefined,
  params: ObjectBakedStyleParams,
): BoxPassthroughMeta | undefined {
  const objectBox: { -readonly [K in keyof ObjectBakedStyleParams]?: ObjectBakedStyleParams[K] } = {};
  if (params.rawEffects !== undefined) objectBox.rawEffects = params.rawEffects;
  if (params.bakeInsets !== undefined) objectBox.bakeInsets = params.bakeInsets;
  if (Object.keys(objectBox).length === 0) return meta;
  return { ...meta, objectBox };
}

export interface BoxFlip {
  readonly flipHorizontal?: true;
  readonly flipVertical?: true;
}

// An object/background/smart-object flip lives in the drawable's `templateMeta`; keys only for a real flip.
export function boxFlipOf(source: { readonly flipHorizontal?: boolean; readonly flipVertical?: boolean } | undefined): BoxFlip {
  return {
    ...(source?.flipHorizontal === true && { flipHorizontal: true }),
    ...(source?.flipVertical === true && { flipVertical: true }),
  };
}

export function boxFlipMeta(box: { readonly flipHorizontal?: boolean; readonly flipVertical?: boolean }): {
  readonly templateMeta?: BoxFlip;
} {
  const flip = boxFlipOf(box);
  return Object.keys(flip).length > 0 ? { templateMeta: flip } : {};
}

/** Inverse of `buildObjectBakedStyleMeta` — only the keys the bag carries, ready to spread into `params`. */
export function objectBakedStyleParams(meta: BoxPassthroughMeta | undefined): ObjectBakedStyleParams {
  const bag = meta?.objectBox;
  return {
    ...(bag?.rawEffects !== undefined && { rawEffects: bag.rawEffects }),
    ...(bag?.bakeInsets !== undefined && { bakeInsets: bag.bakeInsets }),
  };
}

/**
 * IMAGE-only fields the drawable has no slot for. `color`/`orientation` sit on `CustomImageTemplateBox`
 * itself, `fileTag`/`sourceRef`/`versions` on its params; the design-import path writes all five and the
 * editor reads none, so without this bag every re-encode of an imported design dropped them (prod: 1822
 * books carry `color`, 1785 `orientation`, 1074 `fileTag`). `frameStrokeBaked` rides here for the same
 * reason: the template projection stamps it, the renderers read it off the box, the drawable has no slot.
 */
export function buildImagePassthroughMeta(box: CustomImageTemplateBox): BoxPassthroughMeta | undefined {
  const boxMeta = buildBoxPassthroughMeta(box);
  const imageBox: { -readonly [K in keyof ImagePassthroughBag]?: ImagePassthroughBag[K] } = {};
  if (box.color !== undefined) imageBox.color = box.color;
  if (box.orientation !== undefined) imageBox.orientation = box.orientation;
  if (box.params.fileTag !== undefined) imageBox.fileTag = box.params.fileTag;
  if (box.params.sourceRef !== undefined) imageBox.sourceRef = box.params.sourceRef;
  if (box.params.versions !== undefined) imageBox.versions = box.params.versions;
  if (box.params.frameStrokeBaked !== undefined) imageBox.frameStrokeBaked = box.params.frameStrokeBaked;
  if (box.params.cornerRadius !== undefined) imageBox.cornerRadius = box.params.cornerRadius;
  if (box.params.rawEffects !== undefined) imageBox.rawEffects = box.params.rawEffects;
  if (box.layerStyleRendering === 'live' && box.params.decorSource !== undefined) imageBox.decorSource = box.params.decorSource;

  if (Object.keys(imageBox).length === 0) return boxMeta;
  return { ...boxMeta, imageBox };
}

type ImagePassthroughBag = NonNullable<BoxPassthroughMeta['imageBox']>;

/** The two box-level keys `buildImagePassthroughMeta` packed, ready to spread onto the box. */
export function imagePassthroughBox(meta: BoxPassthroughMeta | undefined): Pick<CustomImageTemplateBox, 'color' | 'orientation'> {
  return { color: meta?.imageBox?.color, orientation: meta?.imageBox?.orientation };
}

/** The params-level keys `buildImagePassthroughMeta` packed, ready to spread into `params`. */
export function imagePassthroughParams(
  meta: BoxPassthroughMeta | undefined,
): Pick<
  ImageBoxParams,
  'fileTag' | 'sourceRef' | 'versions' | 'frameStrokeBaked' | 'cornerRadius' | 'rawEffects' | 'decorSource'
> {
  return {
    fileTag: meta?.imageBox?.fileTag,
    sourceRef: meta?.imageBox?.sourceRef,
    versions: meta?.imageBox?.versions,
    frameStrokeBaked: meta?.imageBox?.frameStrokeBaked,
    cornerRadius: meta?.imageBox?.cornerRadius,
    rawEffects: meta?.imageBox?.rawEffects,
    ...(meta?.imageBox?.decorSource && { decorSource: meta.imageBox.decorSource }),
  };
}

const TEXT_PASSTHROUGH_KEYS = [
  'superscriptSize',
  'superscriptPosition',
  'subscriptSize',
  'subscriptPosition',
  'smallCapSize',
  'useFractionalGlyphWidths',
  'frameOnPenBox',
  'antiAlias',
  'firstBaselineOffset',
  'firstBaselineRule',
  'firstBaselineMin',
  'textLayout',
  'firstLineIndent',
  'startIndent',
  'endIndent',
  'spaceBefore',
  'spaceAfter',
  'autoHyphenate',
  'hyphenatedWordSize',
  'preHyphen',
  'postHyphen',
  'consecutiveHyphens',
  'hyphenationZone',
  'language',
  'wordSpacing',
  'paragraphLetterSpacing',
  'glyphSpacing',
  'leadingType',
  'everyLineComposer',
  'hanging',
  'styleRuns',
  'paragraphRuns',
  'fontSizePt',
  'lineHeightPt',
  'leading',
] as const satisfies readonly (keyof TextBoxParams)[];

type TextTypographyKey = (typeof TEXT_PASSTHROUGH_KEYS)[number];

/**
 * Абзацные настройки переносов не зависят от шрифта и переключаются в редакторе («Переносы»): смена
 * шрифта их не сбрасывает, хотя остальную PSD-типографику сумки уносит. Правило первой базовой линии
 * абзаца меряется по текущему шрифту — тоже переживает смену, как и её минимум InDesign (em кегля, шрифт не нужен).
 * Правило переноса (`textLayout`) от шрифта не зависит.
 */
const FONT_INDEPENDENT_TEXT_KEYS = [
  'firstBaselineRule',
  'firstBaselineMin',
  'textLayout',
  'autoHyphenate',
  'hyphenatedWordSize',
  'preHyphen',
  'postHyphen',
  'consecutiveHyphens',
  'language',
] as const satisfies readonly TextTypographyKey[];

const isFontIndependent = (key: TextTypographyKey): boolean => (FONT_INDEPENDENT_TEXT_KEYS as readonly string[]).includes(key);
type TextTypographyFields = Partial<Pick<TextBoxParams, TextTypographyKey>>;

/** Packs typography fields + a box-level bag (via `buildBoxPassthroughMeta`) + an `origin.fontName` snapshot for invalidation. */
export function buildTextPassthroughMeta(box: CustomTemplateBoxBase, params: TextBoxParams): TextPassthroughMeta | undefined {
  const typography = buildTextTypographyMeta(params);
  const boxMeta = buildBoxPassthroughMeta(box);
  if (!typography && boxMeta === undefined) return undefined;
  return { ...boxMeta, ...typography, origin: { fontName: params.fontName } };
}

/** The typography half of the bag alone (colour runs, PSD paragraph settings); `undefined` when the params carry none. */
export function buildTextTypographyMeta(params: TextBoxParams): TextPassthroughMeta | undefined {
  const typography: { -readonly [K in TextTypographyKey]?: TextBoxParams[K] } = {};
  for (const key of TEXT_PASSTHROUGH_KEYS) {
    const value = params[key];
    if (value !== undefined) typography[key] = value as never;
  }

  if (Object.keys(typography).length === 0) return undefined;
  return { ...typography, origin: { fontName: params.fontName } };
}

/**
 * `true` when a persisted text box would decode with a non-undefined `templateMeta` bag — i.e. it carries
 * any typography passthrough on `params`, any box-level passthrough field, or raw effects. The template-side
 * edit ops mirror `spread-edit.ops`'s `d.templateMeta` guards through this predicate, so the two sides agree
 * on WHEN a styleRuns write may land.
 */
export function hasTextPassthrough(box: CustomTemplateBoxBase & { params: TextBoxParams }): boolean {
  return (
    TEXT_PASSTHROUGH_KEYS.some(key => box.params[key] !== undefined) ||
    BOX_PASSTHROUGH_KEYS.some(key => box[key] !== undefined) ||
    box.effects !== undefined
  );
}

/**
 * A font-identity change invalidates the whole PSD passthrough bag: this is what a stale `templateMeta`
 * costs a text box on its next encode (`isTextPassthroughMetaStale` → bag dropped whole). Typography
 * params, box-level fields and raw effects go; typed live styles survive
 * because they are editor-owned (`drawable.effects`), not passthrough, and so do the font-independent
 * hyphenation settings (`FONT_INDEPENDENT_TEXT_KEYS`). Template-side ops call this so a fontName
 * change costs a persisted box exactly what it costs a drawable.
 */
export function dropTextPassthrough(box: CustomTextTemplateBox): CustomTextTemplateBox {
  const params: TextBoxParams = { ...box.params };
  for (const key of TEXT_PASSTHROUGH_KEYS) if (!isFontIndependent(key)) delete params[key];
  const next: CustomTextTemplateBox & { -readonly [K in (typeof BOX_PASSTHROUGH_KEYS)[number]]?: unknown } = {
    ...box,
    params,
    effects: mergeDrawableEffects(buildDrawableEffects(box.effects), undefined),
  };
  for (const key of BOX_PASSTHROUGH_KEYS) delete next[key];
  return next;
}

/**
 * `true` when `meta` snapshotted a different fontName than the drawable's live one — must be dropped, not carried forward.
 * A malformed bag (no `origin`) reads as stale too: without its snapshot it cannot prove it belongs to this font.
 */
export function isTextPassthroughMetaStale(currentFontName: string, meta: TextPassthroughMeta | undefined): boolean {
  return meta != null && meta.origin?.fontName !== currentFontName;
}

/**
 * Unpacks ONLY the typography fields onto `TextBoxParams` — box-level fields (psdLayerId, lock, ...)
 * live on `TextPassthroughMeta` too (it extends `BoxPassthroughMeta`) but belong on the box itself via
 * `mergeBoxPassthroughMeta`, not inside `params`. Drops the bag outright (`{}`) when stale.
 */
export function mergeTextPassthroughMeta(currentFontName: string, meta: TextPassthroughMeta | undefined): TextTypographyFields {
  const fields: { -readonly [K in TextTypographyKey]?: TextBoxParams[K] } = {};
  if (!meta) return fields;
  const stale = isTextPassthroughMetaStale(currentFontName, meta);
  for (const key of TEXT_PASSTHROUGH_KEYS) {
    if (stale && !isFontIndependent(key)) continue;
    const value = meta[key];
    if (value !== undefined) fields[key] = value as never;
  }

  return fields;
}

/**
 * Packs the OLD-side text metrics that `TextBoxParams` has no faithful slot for. `fontSize`/
 * `lineHeightPx` are the first editor's own stale duplicates (they diverge from the live
 * `naturalAligns.fontSize`, so they cannot be re-derived), and `lineHeight`/`letterSpacing` are the
 * ratio/absolute pair the codec turns into `lineHeightPx`/`tracking` by dividing by `fontSize` — a
 * division that is not invertible once `fontSize` is not positive.
 */
export function buildLegacyTextMetrics(text: TextParams, fontSize: number): LegacyTextMetrics | undefined {
  const metrics: { -readonly [K in keyof LegacyTextMetrics]?: LegacyTextMetrics[K] } = {};
  if (text.fontSize !== undefined) metrics.fontSize = text.fontSize;
  if (text.lineHeightPx !== undefined) metrics.lineHeightPx = text.lineHeightPx;
  if (!(fontSize > 0)) {
    metrics.lineHeight = text.lineHeight;
    metrics.letterSpacing = text.letterSpacing;
  }

  return Object.keys(metrics).length > 0 ? metrics : undefined;
}

/** Inverse of `buildLegacyTextMetrics` — only the keys the bag actually carried, ready to spread into `drawable.text`. */
export function mergeLegacyTextMetrics(metrics: LegacyTextMetrics | undefined): Pick<TextParams, 'fontSize' | 'lineHeightPx'> {
  return {
    ...(metrics?.fontSize !== undefined ? { fontSize: metrics.fontSize } : {}),
    ...(metrics?.lineHeightPx !== undefined ? { lineHeightPx: metrics.lineHeightPx } : {}),
  };
}
